import { useEffect, useMemo } from "react";
import { useFormikContext } from "formik";
import usePrevious from "react-use/lib/usePrevious";
import { diff } from "deep-object-diff";
import { isEmpty } from "lodash";
import { useAppDispatch } from "commons/store/hooks";
import { fetchSurvey, fetchSurveyElements, putElementData } from "features/survey/redux/surveySlice";
import { flatten, debounce } from "lodash";
import { useSnackbar } from "notistack";
import {useParams} from "react-router-dom";

type SurveyElementValue = string | number | boolean | object;

interface FormValues {
  [elementId: string]: SurveyElementValue;
}

type FormikEffectParams = {
  surveyId: string;
  sectionId?: string;
};

const FormikEffect = ({ surveyId, sectionId }: FormikEffectParams) => {
  const { values } = useFormikContext<FormValues>();
  const prevValues = usePrevious(values);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const fetch = useMemo(
    () =>
      debounce(async (diffs: Record<string, SurveyElementValue>) => {
        const promises = Object.entries(diffs).map(([elementId, data]) =>
          dispatch(
            putElementData({
              data: flatten(Array.isArray(data) ? data : [data]),
              elementId,
              surveyId,
            })
          )
        );

        await Promise.all(promises);

        enqueueSnackbar(`Saved ${new Date().toLocaleTimeString()}`, {
          variant: "success",
          autoHideDuration: 2000,
          preventDuplicate: true,
        });

        if (surveyId && sectionId) {
          await dispatch(fetchSurveyElements({ id: surveyId, sectionId }));
        }

        if (surveyId) {
          await dispatch(fetchSurvey(surveyId));
        }
      }, 500),
    [dispatch, surveyId, sectionId]
  );

  useEffect(() => {
    if (prevValues) {
      const diffs = diff(prevValues as object, values as object) as Record<string, SurveyElementValue>;
      if (!isEmpty(diffs)) {
        const normalizedDiffs = Object.entries(diffs).reduce((acc, [elementId, data]) => {
          acc[elementId] =
            typeof data === "object" && data !== null
              ? values[elementId]
              : data;
          return acc;
        }, {} as Record<string, SurveyElementValue>);

        fetch(normalizedDiffs);
      }
    }
  }, [fetch, prevValues, values]);

  return null;
};

export default FormikEffect;
